import { Product } from '@/hooks/use-jurisdiction';

import { read, remove, save } from './async-storage';

export const DEEPLINK_REFERRED_PRODUCT_KEY = 'deeplink_referred_product';

/**
 * Maps a raw vertical string (from AppsFlyer `deep_link_sub2` or URL query param)
 * to a valid Product enum value.
 *
 * @returns The corresponding Product, or null if the value is unrecognised.
 */
export const mapVerticalToProduct = (vertical: string): Product | null => {
    const normalised = vertical.toLowerCase().trim();

    const verticalMap: Record<string, Product> = {
        fantasy: Product.Pickem,
        pickem: Product.Pickem,
        sweeps: Product.Sweeps,
        ssbk: Product.Sweeps,
        arcade: Product.Arcade,
        casino: Product.Casino,
        prediction: Product.Prediction,
        rips: Product.Rips,
    };

    return verticalMap[normalised] ?? null;
};

const KEYCLOAK_VERTICALS: Partial<Record<Product, string>> = {
    [Product.Rips]: 'trading_cards',
};

export const mapProductToKeycloakVertical = (product: Product): string => KEYCLOAK_VERTICALS[product] ?? product;

/**
 * Persists the deeplink-referred product to AsyncStorage.
 * Called when a deep link or URL with a `deep_link_sub2` parameter is received.
 */
export const saveDeeplinkReferredProduct = async (product: Product): Promise<void> => {
    await save(DEEPLINK_REFERRED_PRODUCT_KEY, product);
};

/**
 * Reads the deeplink-referred product from AsyncStorage.
 * @returns The stored Product, or null if none is stored.
 */
export const readDeeplinkReferredProduct = async (): Promise<Product | null> => {
    const value = await read(DEEPLINK_REFERRED_PRODUCT_KEY);
    if (value && Object.values(Product).includes(value)) {
        return value as Product;
    }
    return null;
};

/**
 * Removes the deeplink-referred product from AsyncStorage.
 * Should be called after KYC completes successfully (either IDComply or Trustly).
 */
export const removeDeeplinkReferredProduct = async (): Promise<void> => {
    await remove(DEEPLINK_REFERRED_PRODUCT_KEY);
};
